import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RegisterItemList } from '../RegisterItemList';
import type { Item } from '../RegisterItemList';
import { peopleApi } from '../../services/api';
import type { Person, ActivityStatus, DataField, TransferRecord } from '../../types/models';
import {
  ACTIVITY_STATUSES,
  ACTIVITY_STATUS_LABELS,
  ACTIVITY_STATUS_TONE,
  PROCESSOR_ROLE_LABELS,
  PROCESSOR_ROLE_TONE,
  DPIA_VERDICT_LABELS,
  DPIA_VERDICT_TONE,
  SUBJECT_VOLUME_LABELS,
} from '../../constants/privacy';

/** Tile icon per lifecycle status — the stat row above the table. */
const ACTIVITY_STATUS_ICONS: Record<ActivityStatus, string> = {
  draft:   'ri-draft-line',
  active:  'ri-shield-check-line',
  retired: 'ri-archive-line',
};

const Dash: React.FC = () => <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>;

/** Status is a stored code (draft/active/retired), so map it to label + tone. */
const statusChip = (status: string): React.ReactNode => {
  const key = status as ActivityStatus;
  const label = ACTIVITY_STATUS_LABELS[key] || status;
  const tone = ACTIVITY_STATUS_TONE[key] || 'neutral';
  return <span className={`np-chip np-tone-${tone}`}>{label}</span>;
};

// `dataFields` / `transfers` arrive untyped off the generic register item — narrow once, here.
const fieldsOf = (item: Item): DataField[] =>
  Array.isArray(item.dataFields) ? (item.dataFields as DataField[]) : [];

const transfersOf = (item: Item): TransferRecord[] =>
  Array.isArray(item.transfers) ? (item.transfers as TransferRecord[]) : [];

const hasSpecialCategory = (item: Item): boolean =>
  fieldsOf(item).some((f) => f.isSpecialCategory);

/**
 * A transfer with no Chapter V mechanism. This is the single most reportable state a ROPA row
 * can be in, so it gets its own stat tile and a loud chip rather than being buried in the
 * detail page — it also blocks activation server-side.
 */
const hasUnlawfulTransfer = (item: Item): boolean =>
  transfersOf(item).some((t) => t.mechanism === 'none');

export const ProcessingActivityList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<ActivityStatus | ''>('');
  const [screeningFilter, setScreeningFilter] = useState<string>('');

  // Activities store only an ownerId; the People register owns the display name.
  // Shares the ['people'] cache key with the rest of the app, so this is free.
  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const peopleById = useMemo(() => {
    const map = new Map<string, string>();
    people.forEach((p) => map.set(p.personId, p.personName));
    return map;
  }, [people]);

  // Falls back to the raw id so a deleted person leaves a traceable value rather than a blank.
  const ownerLabel = (item: Item): string => {
    const id = String(item.ownerId || '');
    if (!id) return '';
    return peopleById.get(id) || id;
  };

  return (
    <RegisterItemList
      headerActions={
        <Link className="np-btn np-btn-ghost" to="/processing-activities/report">
          <i className="ri-file-pdf-2-line"></i>Export PDF
        </Link>
      }
      title="Processing Activities"
      iconClass="ri-database-2-line"
      listPath="/processing-activities"
      detailPathPrefix="/processing-activities"
      idField="activityId"
      titleField="name"
      statusField="status"
      statusChip={statusChip}
      statCards={(items) => [
        ...ACTIVITY_STATUSES.map((s) => ({
          label: ACTIVITY_STATUS_LABELS[s],
          value: items.filter((item) => item.status === s).length,
          icon: ACTIVITY_STATUS_ICONS[s],
          tone: ACTIVITY_STATUS_TONE[s],
        })),
        {
          label: 'Special category',
          value: items.filter(hasSpecialCategory).length,
          icon: 'ri-alert-line',
          tone: 'danger',
        },
        {
          label: 'Transfers to fix',
          value: items.filter(hasUnlawfulTransfer).length,
          icon: 'ri-global-line',
          tone: 'warning',
        },
      ]}
      extraFilters={[
        {
          id: 'activity-status',
          control: (
            <select
              className="np-select"
              style={{ width: 'auto', maxWidth: 220 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ActivityStatus | '')}
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              {ACTIVITY_STATUSES.map((s) => (
                <option key={s} value={s}>{ACTIVITY_STATUS_LABELS[s]}</option>
              ))}
            </select>
          ),
          predicate: (item) => !statusFilter || item.status === statusFilter,
        },
        {
          id: 'activity-screening',
          control: (
            <select
              className="np-select"
              style={{ width: 'auto', maxWidth: 220 }}
              value={screeningFilter}
              onChange={(e) => setScreeningFilter(e.target.value)}
              aria-label="Filter by DPIA screening"
            >
              <option value="">Any screening state</option>
              <option value="unscreened">Not screened</option>
              <option value="required">DPIA required</option>
              <option value="discretionary">DPIA discretionary</option>
              <option value="not_required">DPIA not required</option>
            </select>
          ),
          predicate: (item) => {
            if (!screeningFilter) return true;
            const verdict = String(item.dpiaVerdict || '');
            // 'unscreened' is the empty verdict — the state a DPO most needs to find.
            if (screeningFilter === 'unscreened') return !verdict;
            return verdict === screeningFilter;
          },
        },
      ]}
      extraColumns={[
        {
          header: 'Role',
          width: '150px',
          render: (item) => {
            const role = String(item.role || '') as keyof typeof PROCESSOR_ROLE_LABELS;
            const label = PROCESSOR_ROLE_LABELS[role];
            if (!label) return <Dash />;
            return <span className={`np-chip np-tone-${PROCESSOR_ROLE_TONE[role]}`}>{label}</span>;
          },
          sortValue: (item) => String(item.role || ''),
        },
        {
          header: 'Owner',
          width: '180px',
          render: (item) => {
            const label = ownerLabel(item);
            return label ? <span className="np-sub">{label}</span> : <Dash />;
          },
          sortValue: (item) => ownerLabel(item),
        },
        {
          header: 'Data subjects',
          width: '160px',
          render: (item) => {
            const band = String(item.dataSubjectVolume || '');
            const categories = Array.isArray(item.dataSubjectCategories)
              ? (item.dataSubjectCategories as string[])
              : [];
            if (!band && categories.length === 0) return <Dash />;
            return (
              <span className="d-flex align-items-center gap-2">
                <span className="np-sub">{SUBJECT_VOLUME_LABELS[band] ?? band}</span>
                {categories.length > 0 && (
                  <span className="np-chip np-tone-neutral" title={categories.join(', ')}>
                    {categories.length}
                  </span>
                )}
              </span>
            );
          },
          sortValue: (item) => String(item.dataSubjectVolume || ''),
        },
        {
          header: 'Data',
          width: '150px',
          render: (item) => {
            const fields = fieldsOf(item);
            if (fields.length === 0) return <Dash />;
            const special = fields.filter((f) => f.isSpecialCategory).length;
            return (
              <span className="d-flex align-items-center gap-2">
                <span className="np-tabular">{fields.length}</span>
                {special > 0 && (
                  <span
                    className="np-chip np-tone-danger"
                    title={`${special} special category (Article 9)`}
                  >
                    <i className="ri-alert-line"></i>{special}
                  </span>
                )}
              </span>
            );
          },
          sortValue: (item) => fieldsOf(item).length,
        },
        {
          header: 'Transfers',
          width: '130px',
          render: (item) => {
            const transfers = transfersOf(item);
            if (transfers.length === 0) return <Dash />;
            const unlawful = transfers.filter((t) => t.mechanism === 'none').length;
            return (
              <span className="d-flex align-items-center gap-2">
                <span className="np-tabular">{transfers.length}</span>
                {unlawful > 0 && (
                  <span
                    className="np-chip np-tone-danger"
                    title={`${unlawful} with no Chapter V transfer mechanism`}
                  >
                    <i className="ri-error-warning-line"></i>{unlawful}
                  </span>
                )}
              </span>
            );
          },
          sortValue: (item) => transfersOf(item).length,
        },
        {
          header: 'DPIA',
          width: '160px',
          render: (item) => {
            const verdict = String(item.dpiaVerdict || '') as keyof typeof DPIA_VERDICT_LABELS;
            const label = DPIA_VERDICT_LABELS[verdict] ?? DPIA_VERDICT_LABELS[''];
            const tone = DPIA_VERDICT_TONE[verdict] ?? 'neutral';
            return <span className={`np-chip np-tone-${tone}`}>{label}</span>;
          },
          sortValue: (item) => String(item.dpiaVerdict || ''),
        },
      ]}
    />
  );
};

export default ProcessingActivityList;
